import React, { useState } from "react";

import DotsHorizontal from "@uui/DotsHorizontal";
import EditIcon from "@uui/Edit02";
import SendIcon from "@uui/Send01";
import TrashIcon from "@uui/Trash02";

import Typography from "@mui/material/Typography";

import Table from "@app/analytics/components/Table";
import TableBody from "@app/analytics/components/Table/TableBody";
import TableCell from "@app/analytics/components/Table/TableCell";
import TableHead from "@app/analytics/components/Table/TableHead";
import TableRow from "@app/analytics/components/Table/TableRow";
import useWebhooks from "@app/analytics/hooks/integrations/useWebhooks";
import { IntegrationMenu } from "@app/analytics/Integrations/components/styles";
import type { Webhook } from "@app/analytics/types/webhookTypes";
import useDataTable, { HeadCell } from "@app/common/hooks/useDataTable";
import { toast } from "@app/common/hooks/useToasts";
import useAnchorElement from "@app/utils/hooks/useAnchorElement";

import ConfirmationModal from "@rui/ConfirmationModal";
import IconButton from "@rui/IconButton";
import MenuItem from "@rui/MenuItem";
import Stack from "@rui/Stack";
import Switch from "@rui/Switch";

import AddOrEditWebhookModal from "./AddOrEditWebhookModal";
import {
  WEBHOOK_STATUS_LABEL,
  WEBHOOK_STATUS_SORT_IMPORTANCE,
  TRIGGER_LABEL,
} from "./constants";
import { StatusLabel } from "./styles";

export const HEAD_CELLS: HeadCell<Webhook>[] = [
  {
    id: "name",
    label: "Webhook",
  },
  {
    id: "updatedOn",
    label: "Last Execution",
  },
  {
    id: "status",
    label: "Status",
    sortBy: ({ status }) => WEBHOOK_STATUS_SORT_IMPORTANCE.get(status),
  },
  {
    id: "actions",
    label: "",
    align: "right",
    sortable: false,
  },
];

const WebhooksTableRow = ({ webhook }: { webhook: Webhook }) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [editing, setEditing] = useState(false);

  const { anchorElement, open, onClick, onClose } = useAnchorElement();

  const { sendTestRequest, updateWebhook, deleteWebhook } = useWebhooks();

  const trigger = webhook.trigger ?? "meeting_end";
  const artifact = webhook.artifact ?? "meeting notes";
  const webhookIsActive = webhook.status !== "inactive";

  const handleClickSendTest = () => {
    onClose();
    sendTestRequest.mutate(webhook);
  };

  const handleClickEdit = () => {
    onClose();
    setEditing(true);
  };

  const handleCloseModal = () => setConfirmingDelete(false);

  const handleConfirmDelete = () => {
    deleteWebhook.mutate(webhook.id, {
      onSuccess: () => {
        handleCloseModal();
        toast.success("Webhook successfully deleted!");
      },
      onError: () => {
        handleCloseModal();
        toast.error(
          "Something went wrong and your webhook could not be deleted. Please try again later or contact support@read.ai if the problem persists."
        );
      },
    });
  };

  const handleClickDelete = () => {
    onClose();
    setConfirmingDelete(true);
  };

  const handleToggleStatus = (e: React.ChangeEvent, checked: boolean) => {
    updateWebhook.mutate(
      { id: webhook.id, status: checked ? "active" : "inactive" },
      {
        onError: () => {
          toast.error(
            `Something went wrong and your webhook could not be ${
              checked ? "activated" : "deactivated"
            }. Please try again later or contact support@read.ai if the problem persists.`
          );
        },
      }
    );
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Stack spacing={0.5}>
            <Typography variant="bodySecondaryReg">{webhook.name}</Typography>
            <Typography variant="labelReg" color="shades.neutral.600">
              On {TRIGGER_LABEL[trigger]}, send {artifact}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <Stack>
            <Typography variant="bodySecondaryReg">
              {webhook.updatedOn.toFormat("D")}{" "}
              {webhook.updatedOn.toFormat("h:mm a")}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>
          <StatusLabel status={webhook.status}>
            {WEBHOOK_STATUS_LABEL[webhook.status]}
          </StatusLabel>
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
          <Switch checked={webhookIsActive} onChange={handleToggleStatus} />
          <IconButton onClick={onClick}>
            <DotsHorizontal />
          </IconButton>
        </TableCell>
      </TableRow>

      <IntegrationMenu open={open} onClose={onClose} anchorEl={anchorElement}>
        <MenuItem icon={<SendIcon />} onClick={handleClickSendTest}>
          Send test request
        </MenuItem>
        <MenuItem icon={<EditIcon />} onClick={handleClickEdit}>
          Edit webhook
        </MenuItem>
        <MenuItem icon={<TrashIcon />} onClick={handleClickDelete}>
          Delete webhook
        </MenuItem>
      </IntegrationMenu>
      {editing && (
        <AddOrEditWebhookModal
          open
          onClose={() => setEditing(false)}
          webhook={webhook}
        />
      )}
      {confirmingDelete && (
        <ConfirmationModal
          title="Delete webhook?"
          open={confirmingDelete}
          onClose={handleCloseModal}
          onConfirm={handleConfirmDelete}
          loading={deleteWebhook.isPending}
          confirmProps={{ type: "negative", label: "Delete" }}
        >
          <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
            Once you delete this webhook, data will no longer be passed through
            to the URL.
          </Typography>
        </ConfirmationModal>
      )}
    </>
  );
};

const WebhooksTable = ({ webhooks }: { webhooks: Webhook[] }) => {
  const { sortedData, orderBy, handleSort, order } = useDataTable(
    webhooks,
    HEAD_CELLS,
    { defaultOrderBy: "name" }
  );

  return (
    <Table sx={{ bgcolor: "#FFF" }}>
      <TableHead
        headCells={HEAD_CELLS}
        onSort={handleSort}
        order={order}
        orderBy={orderBy}
      />
      <TableBody>
        {sortedData.map((webhook) => (
          <WebhooksTableRow key={webhook.id} webhook={webhook} />
        ))}
      </TableBody>
    </Table>
  );
};

export default WebhooksTable;
